import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';
import { type ContactLink } from '../types';

import { fetchContactLinkByDataExchangeId } from './fetchContactLinkByDataExchangeId';

/**
 * Generates the query options for fetching the most recent contact link by data-exchange ID.
 *
 * @param deId - The UUID of the data exchange.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
// fallow-ignore-next-line unused-export
export const getContactLinkByDataExchangeIdQueryOptions = <
  TData = ContactLink,
>({
  deId,
  options,
}: {
  deId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<ContactLink, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: deId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchContactLinkByDataExchangeId(deId, { signal })
      : skipToken,
    queryKey: queryKeysSupplier.getContactLinkByDataExchangeId(deId ?? ''),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query based custom hook for fetching the most recent contact link for a data exchange.
 *
 * @param deId - The UUID of the data exchange. Pass `undefined` to skip the query.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_contact_link_by_de_id_suppliers__de_id__contact_link_get
 */
export const useQueryContactLinkByDataExchangeId = <TData = ContactLink>(
  deId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<ContactLink, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getContactLinkByDataExchangeIdQueryOptions<TData>({ deId, options }),
  );
